import type { IDeploymentConfigurationRepository, IStorageFolderRepository } from "@domain/repositories";
import { TYPES } from "@utils/inversifyJS/types";
import { inject, injectable } from "inversify";
import type { PathTypeSchema, PathVisibilitySchema, SoftwareEnumSchema, SoftwareVersionContextConfiguration, StorageContextConfiguration } from "@domain/schemas/BI";

/**
Deployment-level storage and software-version configuration (Admin-only) - backs the Settings page.
*/
@injectable()
export class DeploymentConfigurationService {
  @inject(TYPES.IDeploymentConfigurationRepository)
  private _deploymentConfigurationRepository!: IDeploymentConfigurationRepository;

  @inject(TYPES.IStorageFolderRepository)
  private _storageFolderRepository!: IStorageFolderRepository;

  /**
  Level-agnostic - browses the facet's own container, not a resolved deployment path. Backs the
  "Browse" folder picker; manual path entry remains available regardless of what this returns.
  */
  async listFoldersAsync(pathType: PathTypeSchema, folder: string): Promise<string[]> {
    return this._storageFolderRepository.listFoldersAsync(pathType, folder);
  }

  async getStorageConfigurationAsync(): Promise<StorageContextConfiguration> {
    return this._deploymentConfigurationRepository.getStorageConfigurationAsync();
  }

  async addStoragePathAsync(pathType: PathTypeSchema, path: string, visibility: PathVisibilitySchema): Promise<StorageContextConfiguration> {
    return this._deploymentConfigurationRepository.addStoragePathAsync(pathType, path, visibility);
  }

  async removeStoragePathAsync(pathId: string): Promise<StorageContextConfiguration> {
    return this._deploymentConfigurationRepository.removeStoragePathAsync(pathId);
  }

  async setStoragePathValueAsync(pathId: string, path: string): Promise<StorageContextConfiguration> {
    return this._deploymentConfigurationRepository.setStoragePathValueAsync(pathId, path);
  }

  async setStorageResultsPathAsync(resultsPath: string): Promise<StorageContextConfiguration> {
    return this._deploymentConfigurationRepository.setStorageResultsPathAsync(resultsPath);
  }

  async getSoftwareVersionConfigurationAsync(): Promise<SoftwareVersionContextConfiguration> {
    return this._deploymentConfigurationRepository.getSoftwareVersionConfigurationAsync();
  }

  async addSoftwareVersionAsync(software: SoftwareEnumSchema, version: string): Promise<SoftwareVersionContextConfiguration> {
    return this._deploymentConfigurationRepository.addSoftwareVersionAsync(software, version);
  }

  async removeSoftwareVersionAsync(versionId: string): Promise<SoftwareVersionContextConfiguration> {
    return this._deploymentConfigurationRepository.removeSoftwareVersionAsync(versionId);
  }

  async setSoftwareVersionDefaultAsync(software: SoftwareEnumSchema, version: string): Promise<SoftwareVersionContextConfiguration> {
    return this._deploymentConfigurationRepository.setSoftwareVersionDefaultAsync(software, version);
  }

  async clearSoftwareVersionDefaultAsync(software: SoftwareEnumSchema): Promise<SoftwareVersionContextConfiguration> {
    return this._deploymentConfigurationRepository.clearSoftwareVersionDefaultAsync(software);
  }
}
